import { useUserDetail } from '@/lib/queries';
import type { AppActivity, UserDetail } from '@/lib/types';
import { Avatar } from '@/components/Avatar';
import { Modal } from '@/components/Modal';

interface Props {
  upn: string | null;
  onClose: () => void;
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function daysSince(d: string | null): number | null {
  if (!d) return null;
  const ms = Date.now() - new Date(d).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function statusFor(days: number | null): { color: string; tone: string } {
  if (days === null) return { color: 'bg-ink-700', tone: 'text-ink-600' };
  if (days <= 7) return { color: 'bg-emerald-500', tone: 'text-emerald-400' };
  if (days <= 30) return { color: 'bg-amber-500', tone: 'text-amber-400' };
  return { color: 'bg-red-500', tone: 'text-red-400' };
}

function AppCell({ app }: { app: AppActivity }) {
  const days = daysSince(app.last_activity);
  const { color, tone } = statusFor(days);
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2.5">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${color}`} />
        <span className="text-sm text-ink-100">{app.label}</span>
      </div>
      {app.last_activity ? (
        <div className="text-right">
          <div className="text-xs tabular-nums text-ink-300">
            {fmtDate(app.last_activity)}
          </div>
          <div className={`text-[0.65rem] tabular-nums ${tone}`}>
            {days === 0 ? 'hoy' : `hace ${days} d`}
          </div>
        </div>
      ) : (
        <div className="text-xs text-ink-700">sin uso</div>
      )}
    </div>
  );
}

function HeaderIdentity({ data }: { data: UserDetail }) {
  const lastDays = daysSince(data.last_activity_date);
  const { tone } = statusFor(lastDays);
  return (
    <section className="px-8 py-7">
      <div className="flex items-start gap-4">
        <Avatar upn={data.upn} fullName={data.full_name} size="lg" />
        <div className="min-w-0">
          <h2 className="truncate text-2xl font-light tracking-tight text-ink-50">
            {data.full_name}
          </h2>
          <div className="mt-0.5 truncate text-sm text-ink-500">{data.upn}</div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {data.business_line ? (
              <span className="border border-ink-700 px-2 py-0.5 text-ink-200">
                {data.business_line}
              </span>
            ) : null}
            {data.job_title ? (
              <span className="text-ink-400">{data.job_title}</span>
            ) : null}
          </div>
        </div>
      </div>

      <dl className="mt-7 grid grid-cols-4 gap-x-6 gap-y-4 text-sm">
        <div>
          <dt className="text-[0.65rem] uppercase tracking-widest text-ink-500">
            Departamento
          </dt>
          <dd className="mt-1 text-ink-200">{data.department ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-[0.65rem] uppercase tracking-widest text-ink-500">Sede</dt>
          <dd className="mt-1 text-ink-200">{data.location ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-[0.65rem] uppercase tracking-widest text-ink-500">Alta</dt>
          <dd className="mt-1 tabular-nums text-ink-200">{fmtDate(data.hire_date)}</dd>
        </div>
        <div>
          <dt className="text-[0.65rem] uppercase tracking-widest text-ink-500">
            Última actividad
          </dt>
          <dd className={`mt-1 tabular-nums ${tone}`}>
            {fmtDate(data.last_activity_date)}
          </dd>
        </div>
      </dl>
    </section>
  );
}

export function UserDetailDrawer({ upn, onClose }: Props) {
  const { data, isLoading, error } = useUserDetail(upn);

  return (
    <Modal
      open={upn !== null}
      onClose={onClose}
      ariaLabel="Detalle de usuario"
      maxWidth="2xl"
    >
      {isLoading || !data ? (
        <div className="grid h-96 place-items-center text-ink-500">
          {error ? 'Error al cargar' : 'Cargando…'}
        </div>
      ) : (
        <>
          <HeaderIdentity data={data} />

          <div className="grid grid-cols-1 border-t border-ink-800 md:grid-cols-2">
            <section className="bg-ink-900/40 px-8 py-6 md:border-r md:border-ink-800">
              <div className="flex items-center justify-between">
                <div className="label">Licencia Copilot</div>
                <span
                  className={`inline-flex items-center gap-2 border px-2 py-0.5 text-[0.65rem] uppercase tracking-widest ${
                    data.has_license
                      ? 'border-emerald-700/50 text-emerald-400'
                      : 'border-ink-700 text-ink-500'
                  }`}
                >
                  <span
                    className={`inline-block h-1.5 w-1.5 rounded-full ${
                      data.has_license ? 'bg-emerald-500' : 'bg-ink-700'
                    }`}
                  />
                  {data.has_license ? 'Asignada' : 'Sin licencia'}
                </span>
              </div>
              {data.has_license ? (
                <div className="mt-4">
                  <div className="font-mono text-sm text-ink-100">
                    {data.license_sku ?? '—'}
                  </div>
                  <div
                    className="mt-1 text-xs text-ink-500"
                    title="Primera fecha en que el extractor detectó la licencia. No es la fecha real de asignación (Graph no la expone sin auditoría)."
                  >
                    Detectada desde {fmtDate(data.license_assigned_at)}
                  </div>
                </div>
              ) : (
                <div className="mt-3 text-sm text-ink-500">
                  Este usuario no tiene licencia Copilot asignada.
                </div>
              )}
            </section>

            <section className="px-8 py-6">
              <div className="flex items-baseline justify-between">
                <div className="label">Uso por app · 30d</div>
                <div className="text-[0.65rem] tabular-nums text-ink-600">
                  {data.apps.filter((a) => a.last_activity).length} / {data.apps.length}
                </div>
              </div>
              <div className="mt-3 divide-y divide-ink-800/60">
                {data.apps.map((a) => (
                  <AppCell key={a.app} app={a} />
                ))}
              </div>
            </section>
          </div>
        </>
      )}
    </Modal>
  );
}
